/**
 * Board layout: the front of the distribution board, generated from its single-line diagram —
 * DIN rails of n modules (17.5 mm, drawn at true size), devices in board order (main switch,
 * surge protection, main RCD, circuits on the main RCD, then each RCD group starting a row when
 * it fits on one), module widths from the catalogue references, spare space, occupation.
 * Kept in sync like the multi-line diagram (same signature, plus catalogue references).
 */
import {
  applyTransaction,
  newId,
  removeSheetOps,
  type Element,
  type Id,
  type Op,
  type Project,
  tr,
  type Locale,
} from '@elec/domain';
import { analyse, boardCircuits, multiLineSignature, type Analysis, type CircuitInfo } from '@elec/analysis';
import { catalogKind, findRef, modulesOf } from '@elec/catalog';
import { multiLineSheets } from './multiLine.ts';

export const MODULE_MM = 17.5;
const X0 = 45;
const ROW_Y0 = 45;
const ROW_PITCH = 75;
const DEVICE_H = 45;
const ROWS_PER_SHEET = 3;

export interface BoardLayoutOptions {
  modulesPerRow?: number;
}

function deviceLines(el: Element, c: CircuitInfo | undefined, L: Locale): string {
  const p = el.props;
  const kind = catalogKind(el);
  const lines = [el.tag];
  if (kind === 'breaker' || kind === 'rcbo') lines.push(`${p.curve ?? 'C'}${p.ratingA ?? ''}`);
  if (kind === 'rcd' || kind === 'rcbo') lines.push(`${p.sensitivityMa ?? ''} mA ${p.rcdType ?? ''}`.trim());
  if (kind === 'rcd' || kind === 'switch-disconnector') lines.push(`${p.ratingA ?? ''} A`);
  if (kind === 'spd') lines.push(tr(L, 'parafoudre', 'SPD', 'overspanningsbeveiliging'));
  if (c?.code) lines.push(c.code);
  const ref = findRef(p.catalogRef);
  if (ref) lines.push(ref.reference);
  return lines.join('\n');
}

export function generateBoardLayoutOps(
  project: Project,
  fromSheetId: Id,
  opts: BoardLayoutOptions = {},
  analysis: Analysis = analyse(project),
): { ops: Op[]; sheetId: Id; rows: number; used: number } {
  const from = project.sheets[fromSheetId];
  if (!from) return { ops: [], sheetId: '', rows: 0, used: 0 };
  const perRow = opts.modulesPerRow ?? 18;
  const L = project.meta.locale;
  const existing = multiLineSheets(project, fromSheetId, 'board-layout');
  const signature = multiLineSignature(analysis, fromSheetId, true);
  const ops: Op[] = [];
  const baseName = `${tr(L, 'Implantation', 'Board layout', 'Bordindeling')} — ${from.name}`;
  const baseOrder = existing[0]?.order ?? from.order + 0.7;
  const sheetIds: Id[] = [];
  const openPart = (n: number): Id => {
    const old = existing[n - 1];
    const id = old?.id ?? newId();
    const source = { kind: 'board-layout' as const, fromSheetId, signature, ...(n > 1 ? { part: n } : {}) };
    const name = n > 1 ? `${baseName} (${n})` : baseName;
    if (old) {
      ops.push(...removeSheetOps(project, id).filter((o) => !(o.op === 'remove' && o.coll === 'sheets')));
      ops.push({ op: 'update', coll: 'sheets', id, patch: { source, name } });
    } else
      ops.push({
        op: 'add',
        coll: 'sheets',
        value: {
          id,
          name,
          kind: 'board',
          paper: 'A3',
          orientation: 'landscape',
          order: baseOrder + (n - 1) / 100,
          source,
        },
      });
    sheetIds.push(id);
    return id;
  };
  let sheetId = openPart(1);
  const add = (x: number, y: number, w: number, h: number, lines: string, style?: string) =>
    ops.push({
      op: 'add',
      coll: 'elements',
      value: {
        id: newId(),
        sheetId,
        symbolId: 'board.module',
        x,
        y,
        rotation: 0,
        mirrored: false,
        tag: '',
        props: { w, h, lines, ...(style ? { style } : {}) },
      },
    });
  const text = (x: number, y: number, t: string, size = 2.8) =>
    ops.push({
      op: 'add',
      coll: 'elements',
      value: {
        id: newId(),
        sheetId,
        symbolId: 'annot.text',
        x,
        y,
        rotation: 0,
        mirrored: false,
        tag: '',
        props: { text: t, size },
      },
    });

  // ---- devices in board order
  const circuits = boardCircuits(analysis, fromSheetId);
  const boardSheets = new Set([fromSheetId, ...circuits.map((c) => c.sheet.id)]);
  const onBoard = Object.values(project.elements).filter((e) => boardSheets.has(e.sheetId));
  const byDevice = new Map(circuits.map((c) => [c.device.id, c]));
  const order = (a: Element, b: Element) =>
    project.sheets[a.sheetId]!.order - project.sheets[b.sheetId]!.order || a.x - b.x || a.y - b.y;
  const origin = circuits.map((c) => c.rcds.at(-1)).find(Boolean);
  const blocks: Element[][] = [];
  const head: Element[] = [
    ...onBoard.filter((e) => e.symbolId === 'iec.switch-disconnector').sort(order),
    ...onBoard.filter((e) => e.symbolId === 'iec.spd').sort(order),
    ...(origin ? [origin] : []),
  ];
  const direct = circuits.filter((c) => !c.rcds[0] || c.rcds[0].id === origin?.id).map((c) => c.device);
  blocks.push([...head, ...direct.sort(order)]);
  const groups = new Map<Id, Element[]>();
  for (const c of circuits) {
    const r = c.rcds[0];
    if (r && r.id !== origin?.id && r.id !== c.device.id)
      groups.set(r.id, [...(groups.get(r.id) ?? []), c.device]);
  }
  for (const [rcdId, devs] of [...groups].sort((a, b) =>
    order(project.elements[a[0]]!, project.elements[b[0]]!),
  ))
    blocks.push([project.elements[rcdId]!, ...devs.sort(order)]);
  const placed = new Set(blocks.flat().map((e) => e.id));
  const others = onBoard
    .filter((e) => !placed.has(e.id) && (catalogKind(e) || e.symbolId === 'iec.time-switch'))
    .sort(order);
  if (others.length) blocks.push(others);

  // ---- rows
  let row = 0;
  let used = 0; // modules used on this row
  let total = 0;
  const rowY = () => ROW_Y0 + (row % ROWS_PER_SHEET) * ROW_PITCH;
  const startRow = () => {
    if (row > 0 && row % ROWS_PER_SHEET === 0) sheetId = openPart(row / ROWS_PER_SHEET + 1);
    const y = rowY();
    add(X0 - 5, y + DEVICE_H / 2 - 3.75, perRow * MODULE_MM + 10, 7.5, '', 'rail');
    used = 0;
  };
  const endRow = () => {
    const y = rowY();
    if (used < perRow)
      add(
        X0 + used * MODULE_MM,
        y,
        (perRow - used) * MODULE_MM,
        DEVICE_H,
        tr(L, 'réserve', 'spare', 'reserve'),
        'spare',
      );
    text(
      X0,
      y + DEVICE_H + 6,
      tr(
        L,
        `Rangée ${row + 1} : ${used} / ${perRow} modules`,
        `Row ${row + 1}: ${used} / ${perRow} modules`,
        `Rij ${row + 1}: ${used} / ${perRow} modules`,
      ),
      2.5,
    );
    row++;
  };
  startRow();
  for (const block of blocks) {
    const width = block.reduce((n, e) => n + Math.max(1, modulesOf(e)), 0);
    // An RCD group starts a new row when it fits on one but not in what is left.
    if (used > 0 && used + width > perRow && width <= perRow) {
      endRow();
      startRow();
    }
    for (const el of block) {
      const m = Math.max(1, modulesOf(el));
      if (used + m > perRow) {
        endRow();
        startRow();
      }
      add(X0 + used * MODULE_MM, rowY(), m * MODULE_MM, DEVICE_H, deviceLines(el, byDevice.get(el.id), L));
      used += m;
      total += m;
    }
  }
  endRow();
  const capacity = row * perRow;
  sheetId = sheetIds[0]!;
  text(
    X0,
    30,
    tr(
      L,
      `${row} rangée(s) de ${perRow} modules · occupation ${total} / ${capacity} (${Math.round((100 * total) / capacity)} %)`,
      `${row} row(s) of ${perRow} modules · ${total} / ${capacity} used (${Math.round((100 * total) / capacity)} %)`,
      `${row} rij(en) van ${perRow} modules · bezetting ${total} / ${capacity} (${Math.round((100 * total) / capacity)} %)`,
    ),
    3,
  );
  for (const old of existing.slice(sheetIds.length)) ops.push(...removeSheetOps(project, old.id));
  applyTransaction(project, { label: '', ops });
  return { ops, sheetId: sheetIds[0]!, rows: row, used: total };
}
